import React, { useState } from 'react';
import { Send, CheckCircle, XCircle, Clock, Eye, Code } from 'lucide-react';

export default function AIDecisionRequest() {
  const [context, setContext] = useState('');
  const [decision, setDecision] = useState('');
  const [optionA, setOptionA] = useState('');
  const [optionB, setOptionB] = useState('');
  const [email, setEmail] = useState('hi@futr.bet');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);

    // Format the full question with context
    const fullQuestion = `
Context: ${context}

Decision needed: ${decision}

Option A: ${optionA}
Option B: ${optionB}

Please click Approve for Option A or Reject for Option B.
    `.trim();

    try {
      const apiUrl = window.location.hostname === 'localhost'
        ? '/api/request-approval'
        : 'https://futr-bet-api.b-9f2.workers.dev/api/request-approval';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: fullQuestion,
          email
        })
      });

      const data = await response.json();
      setResult(data);

      if (data.success && data.token) {
        // Start checking status
        checkApprovalStatus(data.token);
      }
    } catch (error) {
      setResult({
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setLoading(false);
    }
  };

  const checkApprovalStatus = async (token: string) => {
    setCheckingStatus(true);

    const checkInterval = setInterval(async () => {
      try {
        const apiUrl = window.location.hostname === 'localhost'
          ? `/api/approval-status?token=${token}`
          : `https://futr-bet-api.b-9f2.workers.dev/api/approval-status?token=${token}`;

        const response = await fetch(apiUrl);
        const data = await response.json();

        if (data.status !== 'pending') {
          clearInterval(checkInterval);
          setCheckingStatus(false);
          setResult(prev => ({
            ...prev,
            finalStatus: data.status,
            decision: data.status === 'approved' ? 'Option A Selected' : 'Option B Selected'
          }));
        }
      } catch (error) {
        console.error('Error checking status:', error);
      }
    }, 3000);

    // Stop checking after 5 minutes
    setTimeout(() => {
      clearInterval(checkInterval);
      setCheckingStatus(false);
    }, 300000);
  };

  const exampleFill = () => {
    setContext("We've built an AI-human feedback loop that works via email. You can test it at https://futr-bet.pages.dev/approval-test");
    setDecision("What feature should we build next?");
    setOptionA("Build a real-time dashboard showing all pending/approved/rejected requests with live status updates");
    setOptionB("Create an AI agent that uses this approval system to ask permission before making code changes");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 p-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            AI Decision Request
          </h1>
          <p className="text-gray-300">
            Provide context and options for human decision-making
          </p>
          <button
            onClick={exampleFill}
            className="mt-4 text-blue-400 hover:text-blue-300 text-sm underline"
          >
            Fill with example
          </button>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-white mb-2 flex items-center gap-2">
                <Eye size={16} />
                Context (What we've built so far)
              </label>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="We've built an authentication system using magic links..."
                className="w-full p-3 rounded-lg bg-white/10 text-white placeholder-gray-400 border border-white/20 focus:border-purple-400 focus:outline-none"
                rows={3}
                required
              />
            </div>

            <div>
              <label className="block text-white mb-2 flex items-center gap-2">
                <Code size={16} />
                Decision Needed
              </label>
              <input
                value={decision}
                onChange={(e) => setDecision(e.target.value)}
                placeholder="Should we add OAuth or continue with magic links?"
                className="w-full p-3 rounded-lg bg-white/10 text-white placeholder-gray-400 border border-white/20 focus:border-purple-400 focus:outline-none"
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2">
                  ✅ Option A (Approve)
                </label>
                <textarea
                  value={optionA}
                  onChange={(e) => setOptionA(e.target.value)}
                  placeholder="Add Google OAuth integration"
                  className="w-full p-3 rounded-lg bg-white/10 text-white placeholder-gray-400 border border-white/20 focus:border-green-400 focus:outline-none"
                  rows={2}
                  required
                />
              </div>

              <div>
                <label className="block text-white mb-2">
                  ❌ Option B (Reject)
                </label>
                <textarea
                  value={optionB}
                  onChange={(e) => setOptionB(e.target.value)}
                  placeholder="Keep magic links and add 2FA"
                  className="w-full p-3 rounded-lg bg-white/10 text-white placeholder-gray-400 border border-white/20 focus:border-red-400 focus:outline-none"
                  rows={2}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-white mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-3 rounded-lg bg-white/10 text-white placeholder-gray-400 border border-white/20 focus:border-purple-400 focus:outline-none"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading || checkingStatus}
              className="w-full py-3 px-6 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold hover:from-purple-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Clock className="animate-spin" size={20} />
                  Sending Request...
                </>
              ) : checkingStatus ? (
                <>
                  <Clock className="animate-pulse" size={20} />
                  Waiting for Response...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Request Decision
                </>
              )}
            </button>
          </form>

          {result && (
            <div className={`mt-6 p-4 rounded-lg ${
              result.success
                ? 'bg-green-500/20 border border-green-500/50'
                : 'bg-red-500/20 border border-red-500/50'
            }`}>
              <div className="flex items-start gap-3">
                {result.success ? (
                  <CheckCircle className="text-green-400" size={24} />
                ) : (
                  <XCircle className="text-red-400" size={24} />
                )}
                <div className="flex-1">
                  <p className="text-white">{result.message}</p>
                  {result.token && (
                    <div className="mt-3 space-y-2">
                      <p className="text-gray-400 text-sm">
                        Token: <code className="bg-black/30 px-2 py-1 rounded">{result.token}</code>
                      </p>
                      {checkingStatus && (
                        <p className="text-yellow-400 text-sm animate-pulse">
                          ⏳ Checking for response every 3 seconds...
                        </p>
                      )}
                      {result.finalStatus && (
                        <div className={`p-3 rounded ${
                          result.finalStatus === 'approved'
                            ? 'bg-green-500/30 border border-green-500'
                            : 'bg-red-500/30 border border-red-500'
                        }`}>
                          <p className="text-white font-semibold">
                            {result.decision}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
          <h2 className="text-xl font-semibold text-white mb-4">How it works</h2>
          <ol className="space-y-3 text-gray-300">
            <li className="flex items-start gap-2">
              <span className="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">1</span>
              <span>AI provides context about what's been built</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">2</span>
              <span>AI presents two clear options for next steps</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">3</span>
              <span>Human receives email with full context</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">4</span>
              <span>Approve button = Option A, Reject button = Option B</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">5</span>
              <span>AI automatically detects response and continues building</span>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}